import React from 'react'
import ReactMarkdown from 'react-markdown'
import { Link } from 'react-router-dom'
import remarkGfm from 'remark-gfm'

import Typography from '~/components/elements/Typography/Typography'

type MdComponentProps = {
  [key: string]: any
  children?: React.ReactNode
  className?: string
}

const mdComponents = {
  h1: ({ className, children, ...props }: MdComponentProps) => (
    <Typography.H1 className={className} {...props}>
      {children}
    </Typography.H1>
  ),
  h2: ({ className, children, ...props }: MdComponentProps) => (
    <Typography.H2 className={className} {...props}>
      {children}
    </Typography.H2>
  ),
  h3: ({ className, children, ...props }: MdComponentProps) => (
    <Typography.H3 className={className} {...props}>
      {children}
    </Typography.H3>
  ),
  p: ({ className, children, ...props }: MdComponentProps) => (
    <Typography.Paragraph className={className} {...props}>
      {children}
    </Typography.Paragraph>
  ),
  a: ({ className, children, href, ...props }: MdComponentProps) => {
    if (!href) {
      return (
        <a className={className} {...props}>
          {children}
        </a>
      )
    }
    if (href.startsWith('/') || href.startsWith('#')) {
      return (
        <Link className={className} to={href} {...props}>
          {children}
        </Link>
      )
    }
    try {
      const linkUrl = new URL(href)
      const isInternal = linkUrl.hostname.endsWith('rhapsody.vet')
      const hasNullBusinessId =
        linkUrl.search.includes('businessId') &&
        linkUrl.searchParams.get('businessId') === 'None'
      if (hasNullBusinessId) {
        linkUrl.searchParams.delete('businessId')
      }
      if (isInternal) {
        // Use React Router for internal links to avoid re-rendering the chat
        const internalPath = `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`
        return (
          <Link className={className} to={internalPath} {...props}>
            {children}
          </Link>
        )
      }
    } catch (error) {
      // If URL parsing fails, treat as external link
    }

    return (
      <a
        className={className}
        href={href}
        rel="noopener noreferrer"
        target="_blank"
        {...props}
      >
        {children}
      </a>
    )
  },
  ul: ({ className, children, ...props }: MdComponentProps) => (
    <ul className={className} {...props}>
      {children}
    </ul>
  ),
  ol: ({ className, children, ...props }: MdComponentProps) => (
    <ol className={className} {...props}>
      {children}
    </ol>
  ),
  li: ({ className, children, ...props }: MdComponentProps) => (
    <li className={className} {...props}>
      {children}
    </li>
  ),
  blockquote: ({ className, children, ...props }: MdComponentProps) => (
    <blockquote className={className} {...props}>
      {children}
    </blockquote>
  ),
  code: ({ className, children, ...props }: MdComponentProps) => (
    <code className={className} {...props}>
      {children}
    </code>
  ),
  pre: ({ className, children, ...props }: MdComponentProps) => (
    <pre className={className} {...props}>
      {children}
    </pre>
  ),
  hr: ({ className, ...props }: MdComponentProps) => (
    <hr className={className} {...props} />
  ),
  table: ({ className, children, ...props }: MdComponentProps) => (
    <div>
      <table className={className} {...props}>
        {children}
      </table>
    </div>
  ),
  th: ({ className, children, ...props }: MdComponentProps) => (
    <th className={className} {...props}>
      {children}
    </th>
  ),
  td: ({ className, children, ...props }: MdComponentProps) => (
    <td className={className} {...props}>
      {children}
    </td>
  ),
  strong: ({ className, children, ...props }: MdComponentProps) => (
    <strong className={className} {...props}>
      {children}
    </strong>
  ),
  em: ({ className, children, ...props }: MdComponentProps) => (
    <em className={className} {...props}>
      {children}
    </em>
  ),
}

export const MarkdownRenderer: React.FC<{ content: string }> = ({
  content,
}) => (
  <div className="markdown-content">
    <ReactMarkdown components={mdComponents} remarkPlugins={[remarkGfm]}>
      {content}
    </ReactMarkdown>
  </div>
)

export default MarkdownRenderer
